import { getColors } from "@/shared/utils/ganttColors";
import { getMomentTz } from "@dexteel/mesf-core";
import {
	Autocomplete,
	Box,
	CircularProgress,
	type SxProps,
	TextField,
	type Theme,
} from "@mui/material";

const moment = getMomentTz();

export type ProductionEventLike = {
	EventId: number;
	SectionId?: number | null;
	SectionName?: string | null;
	Start: Date | null;
	End: Date | null;
};

export const SectionDot = ({
	sectionId,
	sx,
}: {
	sectionId: number;
	sx?: SxProps<Theme>;
}) => (
	<Box
		component="span"
		sx={[
			{
				backgroundColor: getColors(sectionId).fill,
				borderRadius: "50%",
				display: "inline-block",
				flexShrink: 0,
				height: 10,
				mr: 1,
				width: 10,
			},
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	/>
);

type Item<T> = { kind: "all" } | { kind: "event"; event: T };
const ALL: Item<never> = { kind: "all" };

const getOptionLabel = <T extends ProductionEventLike>(
	item: Item<T>,
	withSection: boolean,
) => {
	if (item.kind === "all") return "All";

	const section =
		withSection && item.event.SectionName ? `${item.event.SectionName} · ` : "";
	const start = item.event.Start
		? moment(item.event.Start).format("MM/DD/YY")
		: "";
	const end = item.event.End
		? moment(item.event.End).format("MM/DD/YY")
		: "Open";

	return `${section}Event #${item.event.EventId} · ${start} - ${end}`;
};

type Props<T> = {
	options: T[];
	value: number | null;
	showSection?: boolean;
	isLoading?: boolean;
	label?: string;
	emptyText?: string;
	allowAll?: boolean;
	disabled?: boolean;
	onChange: (productionEvent: T | null) => void;
};

export const ProductionEventSelector = <T extends ProductionEventLike>({
	options,
	value,
	showSection = false,
	isLoading = false,
	label = "Production Event",
	emptyText = "There are no events for this selection.",
	allowAll = true,
	disabled = false,
	onChange,
}: Props<T>) => {
	if (!isLoading && options.length === 0)
		return (
			<TextField
				label={label}
				value={emptyText}
				variant="outlined"
				size="small"
				disabled
				fullWidth
				sx={{ backgroundColor: (theme) => theme.palette.background.paper }}
			/>
		);

	const items: Item<T>[] = [
		...(allowAll ? [ALL as Item<T>] : []),
		...options.map((event) => ({ kind: "event" as const, event })),
	];

	const selected =
		items.find(
			(item) => item.kind === "event" && item.event.EventId === value,
		) ?? (allowAll ? ALL : null);

	return (
		<Autocomplete
			id="production-event-selector"
			size="small"
			clearOnEscape
			autoHighlight
			options={items}
			getOptionLabel={(item) => getOptionLabel(item, showSection)}
			value={selected}
			disableClearable={!allowAll}
			disabled={isLoading || disabled}
			onChange={(_event, item) =>
				onChange(item !== null && item.kind === "event" ? item.event : null)
			}
			renderOption={(optionProps, item) => (
				<Box
					component="li"
					{...optionProps}
					key={item.kind === "all" ? "all" : item.event.EventId}
					sx={{ alignItems: "center", display: "flex" }}
				>
					{item.kind === "event" &&
						showSection &&
						item.event.SectionId != null && (
							<SectionDot sectionId={item.event.SectionId} />
						)}
					{getOptionLabel(item, showSection)}
				</Box>
			)}
			renderInput={(params) => (
				<TextField
					{...params}
					disabled={disabled}
					label={label}
					variant="outlined"
					size="small"
					fullWidth
					sx={{ backgroundColor: (theme) => theme.palette.background.paper }}
					InputProps={{
						...params.InputProps,
						startAdornment:
							selected?.kind === "event" &&
							showSection &&
							selected.event.SectionId != null ? (
								<SectionDot
									sectionId={selected.event.SectionId}
									sx={{ ml: 0.5, mr: 0 }}
								/>
							) : null,
						endAdornment: (
							<>
								{isLoading ? (
									<CircularProgress color="inherit" size={20} />
								) : null}
								{params.InputProps.endAdornment}
							</>
						),
					}}
				/>
			)}
		/>
	);
};
